import React from 'react';
import { C, CARD_W, INFO_BOX_NODE_H, nx, ny } from '../flowCanvas.constants';

const HEADER_H  = 40;
const PAD_X     = 12;
const LABEL_H   = 13;
const INPUT_H   = 28;
const TEXTAREA_H = 64;

// ─── InfoBoxNode ───────────────────────────────────────────────────────────────
// Inline-editable node for the Info Panel feature flow.
// Displays a Header Copy input and a Body Copy textarea that write directly
// back to the hud / instructions customisation data.
// ─────────────────────────────────────────────────────────────────────────────

export default function InfoBoxNode({
  step,
  selected,
  onClick,
  centreY,
  overrideX,
  overrideY,
  liveCustomisationData,
  onFieldChange,
  onFieldFocus,
}) {
  const x    = overrideX ?? nx(step);
  const y    = overrideY ?? ny(step, centreY);
  const left = x - CARD_W / 2;
  const top  = y - INFO_BOX_NODE_H / 2;

  const cfg    = step?.customisationConfig;
  const tabKey = cfg?.tabKey ?? 'hud';
  const varKey = cfg?.varKey ?? 'instructions';

  const headerVal = liveCustomisationData?.[tabKey]?.[varKey]?.headerCopy?.[0]?.copy ?? '';
  const bodyVal   = liveCustomisationData?.[tabKey]?.[varKey]?.instructionCopy?.[0]?.copy ?? '';

  // Layout rows
  const bodyAreaTop    = top + HEADER_H;
  const headerLabelY   = bodyAreaTop + 10;
  const headerInputY   = headerLabelY + LABEL_H + 4;
  const bodyLabelY     = headerInputY + INPUT_H + 10;
  const bodyInputY     = bodyLabelY + LABEL_H + 4;

  const inputStyle = {
    width: '100%',
    height: '100%',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(145,70,255,0.35)',
    borderRadius: '6px',
    color: C.text,
    fontSize: '10px',
    padding: '0 8px',
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'inherit',
  };

  const textareaStyle = {
    ...inputStyle,
    padding: '6px 8px',
    resize: 'none',
    lineHeight: '1.4',
  };

  return (
    <g className="fc-node fc-node--info-box" onClick={onClick} style={{ cursor: 'pointer' }}>
      {/* Drop shadow */}
      <rect
        x={left + 2} y={top + 4}
        width={CARD_W} height={INFO_BOX_NODE_H}
        rx="10" fill="#000" fillOpacity="0.25"
        style={{ pointerEvents: 'none' }}
      />

      {/* Body */}
      <rect
        x={left} y={top}
        width={CARD_W} height={INFO_BOX_NODE_H}
        rx="10"
        fill={selected ? C.cardSel : C.card}
        stroke={selected ? C.sel : C.border}
        strokeWidth={selected ? 2 : 1}
      />

      {/* Purple header band */}
      <rect
        x={left} y={top}
        width={CARD_W} height={HEADER_H}
        rx="10"
        fill={C.purple}
        style={{ pointerEvents: 'none' }}
      />
      {/* Square off bottom corners of header band */}
      <rect
        x={left} y={top + HEADER_H - 8}
        width={CARD_W} height={8}
        fill={C.purple}
        style={{ pointerEvents: 'none' }}
      />

      {/* Title */}
      <text
        x={left + PAD_X} y={top + HEADER_H / 2 + 1}
        dominantBaseline="middle"
        fontSize="11" fontWeight="700" fill={C.text}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {step.stepTitle || 'Info Box'}
      </text>

      {/* Divider */}
      <line
        x1={left + 1} y1={top + HEADER_H}
        x2={left + CARD_W - 1} y2={top + HEADER_H}
        stroke={C.border} strokeWidth="1"
        style={{ pointerEvents: 'none' }}
      />

      {/* ── Header Copy ── */}
      <text
        x={left + PAD_X} y={headerLabelY + LABEL_H - 2}
        fontSize="9" fontWeight="600" fill={C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        Header Copy
      </text>

      <foreignObject
        x={left + PAD_X}
        y={headerInputY}
        width={CARD_W - PAD_X * 2}
        height={INPUT_H}
        style={{ pointerEvents: 'auto' }}
      >
        <input
          xmlns="http://www.w3.org/1999/xhtml"
          type="text"
          value={headerVal}
          placeholder="Header copy…"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onFocus={() => onFieldFocus?.('Header')}
          onChange={(e) => {
            e.stopPropagation();
            onFieldChange?.('headerCopy', 'copy', e.target.value);
          }}
          style={inputStyle}
        />
      </foreignObject>

      {/* ── Body Copy ── */}
      <text
        x={left + PAD_X} y={bodyLabelY + LABEL_H - 2}
        fontSize="9" fontWeight="600" fill={C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        Body Copy
      </text>

      <foreignObject
        x={left + PAD_X}
        y={bodyInputY}
        width={CARD_W - PAD_X * 2}
        height={TEXTAREA_H}
        style={{ pointerEvents: 'auto' }}
      >
        <textarea
          xmlns="http://www.w3.org/1999/xhtml"
          value={bodyVal}
          placeholder="Body copy…"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onFocus={() => onFieldFocus?.('Body')}
          onChange={(e) => {
            e.stopPropagation();
            onFieldChange?.('instructionCopy', 'copy', e.target.value);
          }}
          style={textareaStyle}
        />
      </foreignObject>
    </g>
  );
}
